import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { useAuth } from '@/shared/auth/useAuth';
import { Alert } from '@/shared/ui/Alert';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { PageHeader } from '@/shared/ui/PageHeader';

import { creditConsumption } from './api';
import {
  ListaDeLimites,
  PadraoPorCorretor,
  ResumoDaEmpresa,
} from './ConsultationLimitsView';
import { ConsumptionReportView } from './ConsumptionReportView';

/** Permissao da leitura do consumo (docs/AUTHORIZATION.md, secao 56). */
export const permissaoDoConsumo = 'credito.consumo.view';

/**
 * Consumo das consultas ao bureau da empresa (Configuracoes, credito, consumo).
 *
 * Mostra cada chamada ao fornecedor feita pela empresa, com o custo pelo preco da
 * plataforma, e os limites de consulta em leitura. A empresa nao edita preco nem limite:
 * o contrato com a Vigidata e da plataforma (ADR 0098). O teto financeiro e a politica de
 * autorizacao ficam nos parametros do motor de credito, grupo "Consumo de consultas".
 *
 * Esconder a tela de quem nao tem a permissao e orientacao: o servidor recusa a
 * leitura do mesmo jeito.
 */
export function CompanyConsumptionPage() {
  const { can } = useAuth();

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Consumo das consultas ao bureau"
        description="Cada chamada a SeguraCoreCredit feita pela empresa, com o tempo de resposta, o resultado e o custo pelo preco vigente no instante."
        actions={
          <Link
            to="/configuracoes"
            className="text-sm font-medium text-brand-600 hover:underline"
          >
            Voltar para as configuracoes
          </Link>
        }
      />
      {can(permissaoDoConsumo) ? (
        <>
          <LimitesDaEmpresa />
          <ConsumptionReportView
            chave="empresa"
            carregar={(filtros, signal) =>
              creditConsumption.tenantReport(filtros, signal)
            }
          />
        </>
      ) : (
        <Alert tone="warning" title="Sem acesso ao consumo">
          O relatorio de consumo das consultas exige a permissao {permissaoDoConsumo}.
          Peca a quem administra a empresa.
        </Alert>
      )}
    </div>
  );
}

/** Os limites da empresa, em leitura: so a administracao da plataforma edita. */
function LimitesDaEmpresa() {
  const limites = useQuery({
    queryKey: ['empresa', 'limites-de-consulta'],
    queryFn: ({ signal }) => creditConsumption.tenantLimits(signal),
  });

  return (
    <Card>
      <CardHeader
        title="Limites de consulta"
        description="Quantas consultas ao bureau a empresa, cada corretor e cada tomador podem fazer. Vale o limite mais restritivo. Para alterar, fale com a administracao da plataforma."
      />
      <CardBody className="flex flex-col gap-4">
        {limites.isError ? (
          <Alert tone="danger" title="Nao foi possivel carregar os limites">
            {limites.error.message}
          </Alert>
        ) : null}
        {limites.data ? (
          <>
            <ResumoDaEmpresa limite={limites.data.company} />
            <PadraoPorCorretor padrao={limites.data.broker_default} />
            <ListaDeLimites itens={limites.data.items} carregando={false} />
          </>
        ) : null}
      </CardBody>
    </Card>
  );
}
